import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './Counters.css';
import { fetchOutageIntervals, outageHours } from './githubOutages';
import {
  atollSlowdowns,
  dataBugAnnoyances,
  databricksEyeRolls,
  isWorkingHours,
  stupidMessageSighs,
  SIGH_RATE_PER_SECOND,
} from './counterMath';

const SINCE_LABEL = 'Since March 2, 2026';
const TICK_MS = 200;
const CLOCK_TICK_MS = 50; // debt-clock cadence

function useNow(intervalMs = TICK_MS) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function useGithubOutages() {
  const [state, setState] = useState({ status: 'loading', intervals: [], incidentCount: 0 });

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchOutageIntervals()
        .then(({ intervals, incidentCount }) => {
          if (!cancelled) setState({ status: 'ready', intervals, incidentCount });
        })
        .catch((error) => {
          console.error('[counters] githubstatus.com fetch failed:', error);
          if (!cancelled) setState((prev) => (prev.status === 'ready' ? prev : { ...prev, status: 'error' }));
        });
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000); // refresh while the tab stays open
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return state;
}

const integer = new Intl.NumberFormat('en-US');
const twoDecimals = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CounterRow({ index, value, unit, label, note, live }) {
  return (
    <section className="border-t-2 border-[#1c1a17] py-8 md:py-12">
      <div className="flex flex-col-reverse gap-6 md:flex-row md:items-end md:justify-between md:gap-12">
        <div className="max-w-xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="ledger-number text-xs font-bold tracking-[0.3em] text-[#8a8175]">
              {String(index).padStart(2, '0')}
            </span>
            {live && (
              <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#a8321f]">
                <span className="ledger-live-dot inline-block h-1.5 w-1.5 rounded-full bg-[#a8321f]" />
                counting
              </span>
            )}
          </div>
          <h2 className="text-xl md:text-3xl font-extrabold leading-tight text-[#1c1a17] uppercase tracking-tight">
            {label}
          </h2>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-[#6b6357]">{note}</p>
        </div>

        <div className="flex shrink-0 items-baseline gap-2 md:justify-end">
          <span className="ledger-number text-[3.25rem] leading-none md:text-[6rem] font-extrabold text-[#1c1a17]">
            {value}
          </span>
          {unit && (
            <span className="text-sm md:text-lg font-bold uppercase tracking-[0.15em] text-[#8a8175]">{unit}</span>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The debt-clock row: label on top, then a full-bleed dark readout. It keeps its
 * own 50ms clock so the rest of the ledger can stay lazy at 200ms, and it is the
 * only row whose low digits are unreadable on purpose.
 */
function SighClockRow({ index, label, note }) {
  const now = useNow(CLOCK_TICK_MS);
  return (
    <section className="border-t-2 border-[#1c1a17] py-8 md:py-12">
      <div className="mb-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-3">
          <span className="ledger-number text-xs font-bold tracking-[0.3em] text-[#8a8175]">
            {String(index).padStart(2, '0')}
          </span>
          <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#a8321f]">
            <span className="ledger-live-dot inline-block h-1.5 w-1.5 rounded-full bg-[#a8321f]" />
            counting
          </span>
        </div>
        <h2 className="text-xl md:text-3xl font-extrabold leading-tight text-[#1c1a17] uppercase tracking-tight">
          {label}
        </h2>
        <p className="mt-3 text-sm md:text-base leading-relaxed text-[#6b6357]">{note}</p>
      </div>

      <div className="ledger-clock overflow-x-auto rounded-sm bg-[#1c1a17] px-4 py-5 md:px-8 md:py-7">
        <div className="ledger-number whitespace-nowrap text-center text-[clamp(1.9rem,8.5vw,5rem)] font-extrabold leading-none text-[#f0d878]">
          {integer.format(stupidMessageSighs(now))}
        </div>
      </div>
      <p className="mt-3 text-right text-[10px] font-bold uppercase tracking-[0.2em] text-[#8a8175]">
        {integer.format(SIGH_RATE_PER_SECOND)} sighs per second
      </p>
    </section>
  );
}

function Counters() {
  const now = useNow();
  const github = useGithubOutages();

  useEffect(() => {
    document.body.classList.add('counters-page');
    return () => document.body.classList.remove('counters-page');
  }, []);

  const outage = useMemo(
    () => (github.status === 'ready' ? outageHours(github.intervals, now) : null),
    [github.status, github.intervals, now],
  );

  const githubValue =
    github.status === 'loading' ? '——' : github.status === 'error' ? 'n/a' : twoDecimals.format(outage);

  const githubNote =
    github.status === 'error'
      ? 'Could not reach githubstatus.com. Ironic, but not counted.'
      : `Live from githubstatus.com — overlapping incidents merged, so simultaneous fires still only burn one hour. ${
          github.status === 'ready' ? `${integer.format(github.incidentCount)} incidents in the window.` : 'Loading…'
        }`;

  const workingHours = isWorkingHours(now);

  return (
    <div className="ledger min-h-screen w-full bg-[#f2efe6] text-[#1c1a17] px-5 md:px-10 py-10 md:py-16">
      <div className="mx-auto max-w-5xl">
        <header className="mb-10 md:mb-16">
          <div className="ledger-rule h-[6px] w-full" />
          <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-4xl md:text-6xl font-extrabold uppercase tracking-tighter leading-none">
                The Attrition Ledger
              </h1>
              <p className="mt-3 text-sm md:text-base font-semibold uppercase tracking-[0.25em] text-[#8a8175]">
                {SINCE_LABEL}
              </p>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-[#6b6357]">
              A running tally of the small indignities of shipping software. Numbers are derived from the clock, not
              stored — reloading does not grant absolution.
            </p>
          </div>
        </header>

        <CounterRow
          index={1}
          value={githubValue}
          unit="hours"
          label="Hours GitHub has had outages"
          note={githubNote}
          live={github.status === 'ready'}
        />

        <CounterRow
          index={2}
          value={integer.format(atollSlowdowns(new Date(now)))}
          unit="times"
          label="Times Jesse has been slowed down by Atoll issues"
          note="Started at 32 on August 26, 2026 and climbs by one every working day. Weekends and US holidays are exempt — Atoll cannot slow down a day that was never going to move."
          live={false}
        />

        <CounterRow
          index={3}
          value={integer.format(dataBugAnnoyances(now))}
          unit="times"
          label="Times Jesse has been annoyed by data bugs"
          note="Started at 500,000 and adds one every five seconds, without pause. The data does not take weekends off, so neither does the annoyance."
          live
        />

        <CounterRow
          index={4}
          value={integer.format(databricksEyeRolls(now))}
          unit="rolls"
          label="Eye rolls at a Databricks table missing for no reason"
          note={`One roll every 137 seconds during working hours, and a slower background hum after dark — the tables still vanish overnight, they just get found in the morning. Currently ${
            workingHours ? 'in office hours: rolling at full speed.' : 'after hours: rolling quietly.'
          }`}
          live
        />

        <SighClockRow
          index={5}
          label="Times Jesse has sighed at a stupid message"
          note="The debt clock of the ledger. Ticking since March 2, 2026 at 4,201 sighs a second and refreshing twenty times a second, because a slower readout would understate the problem."
        />

        <div className="ledger-rule mt-4 h-[6px] w-full" />

        <footer className="mt-8 flex flex-col gap-2 text-xs uppercase tracking-[0.2em] text-[#8a8175] md:flex-row md:items-center md:justify-between">
          <span>Counter 1 is real. Counters 2–5 are mock, but not inaccurate.</span>
          <Link to="/" className="font-bold text-[#1c1a17] no-underline hover:underline">
            ← Back to Spire Wizard
          </Link>
        </footer>
      </div>
    </div>
  );
}

export default Counters;
